import { FileText } from 'lucide-react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import * as React from 'react'

import { ResolvedContentThumbnail } from '@/components/common/resolved-media-image'
import { Skeleton } from '@/components/ui/skeleton'

import { BlogContentType } from '@/enums/blog-content-type.enum'

const QuickShareButtons = dynamic(
  () => import('@/components/common/social-share-buttons').then(mod => mod.QuickShareButtons),
  {
    ssr: false,
    loading: () => <Skeleton className="w-full h-48" />,
  }
)
import type { BlogType } from '@/types/blog.type'

interface BlogCardProps {
  post: BlogType
}

export function BlogCard({ post }: BlogCardProps) {
  const isFile = post.contentType === BlogContentType.FILE

  return (
    <article className="bg-card rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 ease-in-out flex flex-col h-full">
      <Link href={`/blogs/${post.slug}`} key={post.id} className="h-full inline-block">
        <div className="relative">
          <ResolvedContentThumbnail
            thumbnail={post.thumbnailImage}
            alt={post.title}
            width={400}
            height={240}
            className="min-h-[240px] w-full object-contain"
            placeholderClassName="min-h-[240px]"
          />
          {isFile && (
            <span className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground shadow">
              <FileText className="size-3" />
              File
            </span>
          )}
        </div>
        <div className="p-4 flex flex-col justify-between flex-grow">
          <div className="">
            <h2 className="text-xl font-bold mt-2 mb-2">
              <span className="hover:text-primary transition-colors duration-300 ease-in-out">{post.title}</span>
            </h2>
            <p className="text-muted-foreground transition-colors duration-300 ease-in-out hover:text-foreground line-clamp-3">
              {post.metaDescription}
            </p>
          </div>
        </div>
      </Link>

      <div className="px-4 pb-4">
        <QuickShareButtons
          url={`${window.location.origin}/blogs/${post.slug}`}
          title={post.title}
          description={post.metaDescription}
          className="justify-end"
        />
      </div>
    </article>
  )
}

export function BlogSkeleton() {
  return (
    <>
      {[...Array(10)].map((_, i) => (
        <div key={i} className="bg-card rounded-lg shadow-md overflow-hidden">
          <Skeleton className="w-full h-48" />
          <div className="p-4">
            <Skeleton className="h-6 mb-2" />
            <div className="space-y-2">
              <Skeleton className="h-4" />
              <Skeleton className="h-4" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      ))}
    </>
  )
}
